import { Shield, AlertTriangle, AlertCircle, Lock, Calendar, Database, ExternalLink, Globe, HelpCircle, Info, Mail, BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface CheckResult {
  status: "safe" | "caution" | "risk" | "verified_badge";
  domain: string;
  checks: {
    https: boolean | null;
    domainAge: "new" | "established" | "estimated";
    domainAgeMonths?: number;
    domainAgeYear?: number;
    domainAgeEstimated?: boolean;
    publicThreatFeeds: "clean" | "found" | "unknown";
    sslStatus?: "valid" | "not_found" | "unknown";
    verifiedBadge?: boolean;
    badgeToken?: string;
    serverLocation?: {
      country: string;
      organization?: string;
      approximate: boolean;
    };
  };
  error?: string;
}

interface UrlCheckerResultProps {
  result: CheckResult;
}

const statusConfig = {
  verified_badge: {
    icon: BadgeCheck,
    title: "Verified by Transparency Badge",
    description: "This website has passed both automatic threat checks and our eligibility check for the Verified Badge Program.",
    gradient: "gradient-hero",
    bgClass: "bg-accent/50",
    textClass: "text-primary",
    borderClass: "border-primary/40",
  },
  safe: {
    icon: Shield,
    title: "Established Website",
    description: "This domain has positive trust signals and no threats detected. Automated checks only — has not gone through our Verified Badge eligibility check.",
    gradient: "gradient-safe",
    bgClass: "bg-safe-bg",
    textClass: "text-safe",
    borderClass: "border-safe/30",
  },
  caution: {
    icon: AlertTriangle,
    title: "New / Unknown Website",
    description: "Limited data available. This could be a new or less-known website.",
    gradient: "gradient-caution",
    bgClass: "bg-caution-bg",
    textClass: "text-caution",
    borderClass: "border-caution/30",
  },
  risk: {
    icon: AlertCircle,
    title: "Be Careful – Potential Risk",
    description: "Warning signals detected. Exercise extra caution with this website.",
    gradient: "gradient-risk",
    bgClass: "bg-risk-bg",
    textClass: "text-risk",
    borderClass: "border-risk/30",
  },
};

function InfoTooltip({ content }: { content: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className="ml-1 text-muted-foreground hover:text-foreground transition-colors">
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CheckItem({ 
  label, 
  value, 
  status,
  subtext,
  tooltip
}: { 
  label: string; 
  value: string; 
  status: "good" | "warning" | "bad" | "neutral";
  subtext?: string;
  tooltip?: string;
}) {
  const statusColors = {
    good: "text-safe",
    warning: "text-caution",
    bad: "text-risk",
    neutral: "text-muted-foreground",
  };

  const statusIcons = {
    good: <Shield className="h-4 w-4" />,
    warning: <AlertTriangle className="h-4 w-4" />,
    bad: <AlertCircle className="h-4 w-4" />,
    neutral: <Database className="h-4 w-4" />,
  };

  return (
    <div className="py-3 border-b last:border-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
        <span className="text-sm text-muted-foreground flex items-center">
          {label}
          {tooltip && <InfoTooltip content={tooltip} />}
        </span>
        <div className={cn("flex items-center gap-2 text-sm font-medium", statusColors[status])}>
          <span className="shrink-0">{statusIcons[status]}</span>
          <span className="break-words">{value}</span>
        </div>
      </div>
      {subtext && (
        <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
      )}
    </div>
  );
}

function SectionHeader({ icon: Icon, title }: { icon: React.ComponentType<{ className?: string }>; title: string }) {
  return (
    <h5 className="font-medium text-foreground mb-2 flex items-center gap-2 mt-4 first:mt-0">
      <Icon className="h-4 w-4 text-primary" />
      {title}
    </h5>
  );
}

export function UrlCheckerResult({ result }: UrlCheckerResultProps) {
  const baseConfig = statusConfig[result.status];

  // The "caution" bucket is triggered by several different underlying reasons
  // (new domain, unverifiable SSL, unverifiable HTTPS, etc). The static copy
  // always assumed "new/unknown domain" — misleading for an established site
  // like a major retailer whose SSL check simply got blocked by bot protection.
  const isEstablishedDomain = result.checks.domainAge === "established" || result.checks.domainAge === "estimated";
  const hasIncompleteCheck = result.checks.sslStatus === "unknown" || result.checks.https === null;

  const config =
    result.status === "caution" && isEstablishedDomain && hasIncompleteCheck
      ? {
          ...baseConfig,
          title: "Some Checks Incomplete",
          description: "This domain appears established, but we couldn't fully confirm its SSL/HTTPS status right now — this can happen when a site blocks automated checks. Try again shortly, or verify manually.",
        }
      : baseConfig;

  const Icon = config.icon;

  // Determine check statuses
  const httpsStatus = result.checks.https === null ? "neutral" : result.checks.https ? "good" : "bad";
  const httpsValue = result.checks.https === null ? "Could Not Verify" : result.checks.https ? "Yes (Secure)" : "No (Not Secure)";

  const domainAgeStatus = 
    result.checks.domainAge === "established" ? "good" : 
    result.checks.domainAge === "estimated" ? "good" :
    result.checks.domainAge === "new" ? "warning" : "good";
  
  // Build domain age display value
  let domainAgeValue: string;
  if (result.checks.domainAge === "established" && result.checks.domainAgeMonths) {
    const years = Math.floor(result.checks.domainAgeMonths / 12);
    if (result.checks.domainAgeYear) {
      domainAgeValue = `${years} years (Registered in ${result.checks.domainAgeYear})`;
    } else {
      domainAgeValue = `${years}+ years`;
    }
  } else if (result.checks.domainAge === "new") {
    if (result.checks.domainAgeMonths !== undefined) {
      domainAgeValue = `New (${result.checks.domainAgeMonths} months)`;
    } else {
      domainAgeValue = "New (Recently registered)";
    }
  } else if (result.checks.domainAge === "estimated" || result.checks.domainAgeEstimated) {
    domainAgeValue = "Established (Likely several years old)";
  } else {
    domainAgeValue = "Established";
  }

  const publicThreatFeedsStatus = 
    result.checks.publicThreatFeeds === "clean" ? "good" : 
    result.checks.publicThreatFeeds === "found" ? "bad" : "neutral";
  const publicThreatFeedsValue = 
    result.checks.publicThreatFeeds === "clean" ? "No active threats found" : 
    result.checks.publicThreatFeeds === "found" ? "Listed in phishing databases" : "Checking...";

  const sslValue =
    result.checks.sslStatus === "valid" ? "Valid SSL" :
    result.checks.sslStatus === "unknown" ? "Could Not Verify" :
    "SSL Not Found";
  const sslStatus =
    result.checks.sslStatus === "valid" ? "good" :
    result.checks.sslStatus === "unknown" ? "neutral" :
    "bad";

  const serverLocationValue = result.checks.serverLocation 
    ? (result.checks.serverLocation.organization
        ? `${result.checks.serverLocation.country} — ${result.checks.serverLocation.organization}`
        : result.checks.serverLocation.country)
    : "Not available";
  const serverLocationSubtext = result.checks.serverLocation?.approximate 
    ? "Based on IP geolocation" 
    : undefined;
  const getStatusExplanation = () => {
    switch (result.status) {
      case "verified_badge":
        return {
          emoji: "🏅",
          title: "Verified by Transparency Badge",
          lines: [
            "This website applied and passed our eligibility check for the Verified Badge Program.",
            "It passed both automatic threat intelligence scans and our published eligibility criteria.",
            "The badge is actively maintained and revoked if standards are violated."
          ]
        };
      case "safe":
        return {
          emoji: "🟢",
          title: "Established Website",
          lines: [
            "No active malware or phishing threats detected at this time.",
            "This is an automatic check only — it does not include the Verified Badge eligibility check.",
            "For the highest level of trust, look for the Transparency Badge verification."
          ]
        };
      case "caution":
        return isEstablishedDomain && hasIncompleteCheck
          ? {
              emoji: "🟡",
              title: "Some Checks Incomplete",
              lines: [
                "This domain has an established history — that part checked out fine.",
                "One or more live checks (SSL/HTTPS) couldn't be confirmed right now, often because a site blocks automated requests.",
                "This isn't necessarily a red flag — try rechecking later or verify manually."
              ]
            }
          : {
              emoji: "🟡",
              title: "New / Unknown Website",
              lines: [
                "Limited public trust data available.",
                "New or less-known websites may require extra caution."
              ]
            };
      case "risk":
        return {
          emoji: "🔴",
          title: "Be Careful – Potential Risk",
          lines: [
            "Potential risk signals detected.",
            "Avoid sharing sensitive information and proceed with caution."
          ]
        };
    }
  };

  const statusExplanation = getStatusExplanation();

  return (
    <TooltipProvider>
      <div className="w-full max-w-xl mx-auto animate-fade-in">
        {/* Main Status Card */}
        <div className={cn(
          "rounded-2xl border-2 p-5 sm:p-6 md:p-8 mb-6",
          config.bgClass,
          config.borderClass
        )}>
          <div className="flex flex-col items-center text-center">
            <div className={cn("rounded-full p-4 mb-4", config.gradient)}>
              <Icon className="h-10 w-10 text-white" />
            </div>
            <h3 className={cn("text-2xl font-bold mb-2", config.textClass)}>
              {config.title}
            </h3>
            <p className="text-muted-foreground mb-4">
              {config.description}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-background/50 rounded-full px-3 sm:px-4 py-2 max-w-full">
              <ExternalLink className="h-4 w-4 shrink-0" />
              <span className="font-mono text-xs sm:text-sm break-all">{result.domain}</span>
            </div>
            
            {/* Always do your own research notice */}
            <p className="text-xs text-muted-foreground mt-4 italic">
              Always do your own research
            </p>
          </div>
        </div>

        {/* Verified Badge extra info */}
        {result.status === "verified_badge" && (
          <div className="bg-accent/30 border-2 border-primary/30 rounded-xl p-5 mb-6 space-y-3 animate-fade-in">
            <div className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-primary" />
              <p className="font-semibold text-foreground">What This Verification Means</p>
            </div>
            <div className="space-y-2">
              {[
                "Passed automatic threat intelligence scan",
                "Passed our eligibility check for the Verified Badge Program",
                "Actively monitored while badge subscription is active",
                "Badge revoked immediately if safety standards are violated",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="h-3.5 w-3.5 text-primary shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            {result.checks.badgeToken && (
              <Link
                to={`/verify/${result.checks.badgeToken}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary underline underline-offset-2 font-medium"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View full verification details
              </Link>
            )}
          </div>
        )}


        {/* Details Section — hidden for verified badge */}
        {result.status !== "verified_badge" && (
        <div className="bg-card rounded-xl border p-4 sm:p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            Check Details
          </h4>
          
          {/* Security Checks */}
          <SectionHeader icon={Shield} title="Security" />
          <div className="space-y-0">
            <CheckItem 
              label="HTTPS" 
              value={httpsValue} 
              status={httpsStatus} 
            />
            <CheckItem 
              label="SSL Certificate" 
              value={sslValue} 
              status={sslStatus} 
            />
            <CheckItem 
              label="OpenPhish Threat Feed" 
              value={publicThreatFeedsValue} 
              status={publicThreatFeedsStatus} 
            />
          </div>

          {/* Domain Info */}
          <SectionHeader icon={Globe} title="Domain Information" />
          <div className="space-y-0">
            <CheckItem 
              label="Domain Age" 
              value={domainAgeValue} 
              status={domainAgeStatus}
              subtext={result.checks.domainAgeEstimated ? "Based on historical trust and security signals" : undefined}
            />
            <CheckItem 
              label="Server Location (approx)" 
              value={serverLocationValue} 
              status="neutral"
              subtext={serverLocationSubtext}
            />
          </div>
          
          {/* Transparency note */}
          <div className="mt-4 pt-3 border-t border-border/50">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                Some domains do not expose full registration data. In such cases, age is inferred using other public trust signals.
              </p>
            </div>
          </div>
        </div>
        )}

        {/* Owner conversion CTA — shown for established & new/unknown sites */}
        {(result.status === "safe" || result.status === "caution") && (
          <div className="mt-6 bg-gradient-to-br from-primary/10 via-accent/40 to-primary/5 border-2 border-primary/30 rounded-2xl p-5 sm:p-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mx-auto sm:mx-0">
                <BadgeCheck className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="font-semibold text-foreground text-base">
                  Is <span className="text-primary break-all">{result.domain}</span> your website?
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Build instant trust with visitors. Get manually verified and display the Transparency Badge — <span className="font-medium text-foreground">first month free</span>.
                </p>
              </div>
              <Button asChild size="lg" className="btn-verified w-full sm:w-auto shrink-0">
                <Link to="/get-verified">
                  Get Your Website Verified
                  <BadgeCheck className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Security recommendation — shown only for risky results, kept separate from the verdict itself */}
        {result.status === "risk" && (
          <div className="mt-6 bg-card border rounded-2xl p-5 sm:p-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto sm:mx-0">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <p className="font-semibold text-foreground text-base">
                  Stay protected while you browse
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Malwarebytes scans for malware and blocks known-malicious sites in real time — a useful extra layer whenever you're unsure about a link.
                </p>
              </div>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto shrink-0">
                <a
                  href="https://www.tkqlhce.com/click-101877644-15572448"
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                >
                  Get Malwarebytes
                  <ExternalLink className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center sm:text-left">
              This is an affiliate link — we may earn a commission at no extra cost to you. It has no effect on this scan result.{" "}
              <Link to="/affiliate-disclosure" className="underline hover:text-foreground">
                Learn more
              </Link>
            </p>
          </div>
        )}

        {/* How to read this result Section — hidden for verified badge */}
        {result.status !== "verified_badge" && (
        <div className="mt-6 bg-muted/30 rounded-xl border p-4 sm:p-6">
          <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            How to Read This Result
          </h4>
          
          <p className="text-sm text-muted-foreground mb-4">
            This check is based on public technical safety signals available at the time of analysis. It does not verify ownership, official status, or business practices.
          </p>
          
          <div className={cn(
            "rounded-lg p-4 mb-4",
            result.status === "safe" ? "bg-safe-bg border border-safe/20" :
            result.status === "caution" ? "bg-caution-bg border border-caution/20" :
            "bg-risk-bg border border-risk/20"
          )}>
            <div className="flex items-start gap-3">
              <span className="text-xl">{statusExplanation.emoji}</span>
              <div>
                <p className={cn(
                  "font-medium mb-1",
                  result.status === "safe" ? "text-safe" :
                  result.status === "caution" ? "text-caution" :
                  "text-risk"
                )}>
                  {statusExplanation.title}
                </p>
                {statusExplanation.lines.map((line, index) => (
                  <p key={index} className="text-sm text-muted-foreground">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <span>🔁</span>
            <p>Website safety status can change as public data sources update.</p>
          </div>
        </div>
        )}

        {/* Feedback Section */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Are these results inaccurate or do you have suggestions?
          </p>
          <Button
            variant="outline"
            size="sm"
            asChild
          >
            <a 
              href={`mailto:hello@transparencybadge.com?subject=Website%20Check%20Feedback&body=Website%20URL:%20${encodeURIComponent(result.domain)}%0AIssue%20or%20suggestion:%0A`}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Feedback
            </a>
          </Button>
        </div>

        {/* Privacy Notice */}
        <p className="text-xs text-muted-foreground text-center mt-6 max-w-md mx-auto">
          We only check public domain safety signals. No personal data is collected or stored.
        </p>
      </div>
    </TooltipProvider>
  );
}
